import { User, CreateUserInfo, UpdateUserInfo, DeleteUserInfo } from "../../api/typings/Users";
import {
  Profiles,
  ParentProfileInfo,
  StudentProfileInfo,
  SupervisorProfileInfo,
  TutorProfileInfo,
  AdminProfileInfo,
  CreateProfile,
  DeleteProfile,
  RestoreProfile,
  ProfilesInfo,
  ProfilesIdentifier,
  AdminProfile,
  StudentProfile,
  TutorProfile,
  ParentProfile,
  SupervisorProfile,
  AssistantProfileInfo,
  ContentMakerProfileInfo,
  ContentMakerProfile,
  AssistantProfile,
  UpdateProfileRelations,
} from "../../api/typings/Profiles";
import { UserService } from "../services/users/UserService";
import { ProfileService } from "../services/users/ProfileService";
import { ProfileAdminService } from "../services/users/ProfileAdminService";
import { ProfileStudentService } from "../services/users/ProfileStudentService";
import { ProfileTutorService } from "../services/users/ProfileTutorService";
import { ProfileSupervisorService } from "../services/users/ProfileSupervisorService";
import { ProfileParentService } from "../services/users/ProfileParentService";
import { uiStore } from "../stores/UiStore/UiStore";
import { UserRoleEnum } from "../../static/enums/UserRole";
import { GetUsersData } from "../stores/TableStore/TableStore";
import { NormalizedResources } from "../typings/services";
import { formatUsersPhone } from "../../helpers/formatPhone";
import { authStore } from "../stores/AuthStore/AuthStore";
import { assertUnreachable } from "../../helpers/general";
import { ProfileAssistantService } from "../services/users/ProfileAssistantService";
import { ProfileContenterService } from "../services/users/ProfileContenterService";
import { PermissionsUserInfo } from "../../api/typings/Permissions";
import { ValidationErrorHandler } from "../../api/ValidationErrorHandler";
import { HttpStatusCode } from "../../api/users/HttpStatusCodes";

export class BaseUserStore {
  users: Record<string, User> = {};

  usersProfiles: Record<string, Profiles> = {};

  usersProfilesParents: Record<string, ParentProfileInfo> = {};

  usersProfilesStudents: Record<string, StudentProfileInfo> = {};

  usersProfilesSupervisors: Record<string, SupervisorProfileInfo> = {};

  usersProfilesTutors: Record<string, TutorProfileInfo> = {};

  usersProfilesAdmins: Record<string, AdminProfileInfo> = {};

  usersProfilesAssistants: Record<string, AssistantProfileInfo> = {};

  usersProfilesContenters: Record<string, ContentMakerProfileInfo> = {};

  protected userService: UserService;

  protected profileService: ProfileService;

  protected profileAdminService: ProfileAdminService;

  protected profileStudentService: ProfileStudentService;

  protected profileTutorService: ProfileTutorService;

  protected profileSupervisorService: ProfileSupervisorService;

  protected profileParentService: ProfileParentService;

  protected profileAssistantService: ProfileAssistantService;

  protected profileContenterService: ProfileContenterService;

  constructor() {
    this.userService = new UserService();
    this.profileService = new ProfileService();
    this.profileAdminService = new ProfileAdminService(this);
    this.profileStudentService = new ProfileStudentService(this);
    this.profileTutorService = new ProfileTutorService(this);
    this.profileSupervisorService = new ProfileSupervisorService(this);
    this.profileParentService = new ProfileParentService(this);
    this.profileAssistantService = new ProfileAssistantService(this);
    this.profileContenterService = new ProfileContenterService(this);
  }

  createUser = async (data: CreateUserInfo) => {
    try {
      uiStore.startPending();
      const { role, related_as_secondary, related_as_primary, profile } = data;
      const { users } = await this.userService.createUser(data);
      this.setCreatedUser(users);
      const id = Object.keys(users)[0];
      const createProfileData = {
        user: {
          id,
        },
        role,
        profile,
        related_as_primary,
        related_as_secondary,
      };
      await this.createProfile(createProfileData);
    } catch (e) {
      if (e?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        ValidationErrorHandler.showErrorInfo("Не удалось создать пользователя!", e.data.errors);
      } else {
        uiStore.notify({
          message: "Произошла ошибка! Попробуйте перезагрузить страницу и попробовать ещё раз.",
          options: { variant: "error" },
        });
      }
    } finally {
      uiStore.stopPending();
    }
  };

  getUser = async (id: string) => {
    try {
      uiStore.startPending();
      const resources = await this.userService.getUser(id);
      this.setResources(resources);
    } catch (e) {
      uiStore.notify({
        message: "Произошла ошибка! Попробуйте перезагрузить страницу.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  getUsers = async (data: GetUsersData) => {
    try {
      uiStore.startPending();
      const resources = await this.userService.getUsers(data);
      this.setResources(resources);
    } catch (e) {
      uiStore.notify({
        message: "Произошла ошибка! Попробуйте перезагрузить страницу.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  updateUser = async (data: UpdateUserInfo) => {
    try {
      uiStore.startPending();
      const { users } = await this.userService.updateUser(data);
      this.setUsers(users);
      uiStore.notify({ message: "Готово!", options: { variant: "success" } });
    } catch (e) {
      if (e?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        ValidationErrorHandler.showErrorInfo("Произошла ошибка!", e.data.errors);
      } else {
        uiStore.notify({
          message: "Произошла ошибка! Попробуйте перезагрузить страницу и попробовать ещё раз.",
          options: { variant: "error" },
        });
      }
    } finally {
      uiStore.stopPending();
    }
  };

  restoreUser = async (data: DeleteUserInfo<null>) => {
    try {
      uiStore.startPending();
      const resources = await this.userService.restoreUser(data);
      this.setResources(resources);
      uiStore.notify({ message: "Пользователь восстановлен!", options: { variant: "success" } });
    } catch (e) {
      uiStore.notify({
        message: "Не удалось восстановить пользователя! Попробуйте перезагрузить страницу и попробовать ещё раз.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  deleteUser = async (data: DeleteUserInfo) => {
    try {
      uiStore.startPending();
      const { users, users_profiles } = await this.userService.deleteUser(data);
      this.setUsers(users);
      this.usersProfiles = { ...this.usersProfiles, ...users_profiles };
      uiStore.notify({ message: "Пользователь удален!", options: { variant: "success" } });
    } catch (e) {
      uiStore.notify({
        message: "Произошла ошибка! Попробуйте перезагрузить страницу и попробовать ещё раз.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  createProfile = async (data: CreateProfile) => {
    try {
      const { authenticatedUser } = authStore;
      const {
        user: { id },
        profile,
        role,
      } = data;
      uiStore.startPending();
      const resources = await this.profileService.createProfile(data);
      const {
        users_profiles,
        permissions,
        users_profiles_admins,
        users_profiles_parents,
        users_profiles_students,
        users_profiles_supervisors,
        users_profiles_tutors,
        users_profiles_assistants,
        users_profiles_contenters,
      } = resources;
      if (Object.keys(authenticatedUser)[0] === id) {
        authStore.setPermissions(permissions);
      }
      this.usersProfiles = { ...this.usersProfiles, ...users_profiles };
      const createdUserProfile = Object.values(users_profiles).find((profile) => profile.user.id === id);
      this.setUserProfiles(id, [{ type: "users_profiles", id: createdUserProfile!.id }]);
      switch (role) {
        case UserRoleEnum.admin:
          this.usersProfilesAdmins = { ...users_profiles_admins };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as AdminProfile).admin.id,
          } as AdminProfileInfo);
          break;
        case UserRoleEnum.parent:
          this.usersProfilesParents = { ...users_profiles_parents };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as ParentProfile).parent.id,
          } as ParentProfileInfo);
          break;
        case UserRoleEnum.student:
          this.usersProfilesStudents = { ...users_profiles_students };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as StudentProfile).student.id,
          } as StudentProfileInfo);
          break;
        case UserRoleEnum.supervisor:
          this.usersProfilesSupervisors = { ...users_profiles_supervisors };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as SupervisorProfile).supervisor.id,
          } as SupervisorProfileInfo);
          break;
        case UserRoleEnum.contenter:
          this.usersProfilesContenters = { ...users_profiles_contenters };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as ContentMakerProfile).contenter.id,
          } as ContentMakerProfileInfo);
          break;
        case UserRoleEnum.assistant:
          this.usersProfilesAssistants = { ...users_profiles_assistants };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as AssistantProfile).assistant.id,
          } as AssistantProfileInfo);
          break;
        case UserRoleEnum.tutor:
          this.usersProfilesTutors = { ...users_profiles_tutors };
          await this.updateProfile(role, {
            ...profile,
            id: (createdUserProfile as TutorProfile).tutor.id,
          } as TutorProfileInfo);
          break;
        default:
          return assertUnreachable(role);
      }
    } catch (e) {
      if (e?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        ValidationErrorHandler.showErrorInfo("Не удалось создать профиль!", e.data.errors);
      } else {
        uiStore.notify({
          message: "Не удалось создать профиль! Попробуйте перезагрузить страницу.",
          options: { variant: "error" },
        });
      }
    } finally {
      uiStore.stopPending();
    }
  };

  deleteProfile = async (data: DeleteProfile) => {
    try {
      uiStore.startPending();
      const { users, permissions, users_profiles } = await this.profileService.deleteProfile(data);
      this.usersProfiles = { ...this.usersProfiles, ...users_profiles };
      this.setAuthenticatedUserPermissions(users, permissions);
      uiStore.notify({ message: "Готово!", options: { variant: "success" } });
    } catch (e) {
      uiStore.notify({
        message: "Не удалось удалить профиль! Попробуйте перезагрузить страницу.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  restoreProfile = async (data: RestoreProfile) => {
    try {
      uiStore.startPending();
      const { users_profiles, permissions, users } = await this.profileService.restoreProfile(data);
      this.usersProfiles = { ...this.usersProfiles, ...users_profiles };
      this.setAuthenticatedUserPermissions(users, permissions);
      uiStore.notify({ message: "Готово!", options: { variant: "success" } });
    } catch (e) {
      uiStore.notify({
        message: "Не удалось восстановить профиль! Попробуйте перезагрузить страницу.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  updateProfileRelations = async (data: UpdateProfileRelations) => {
    try {
      uiStore.startPending();
      const { users_profiles } = await this.profileService.updateProfileRelations(data);
      this.usersProfiles = { ...this.usersProfiles, ...users_profiles };
    } catch (e) {
      uiStore.notify({
        message: "Не удалось привязать профиль! Попробуйте перезагрузить страницу.",
        options: { variant: "error" },
      });
    } finally {
      uiStore.stopPending();
    }
  };

  updateProfile = async (role: UserRoleEnum, profileInfo: ProfilesInfo) => {
    try {
      uiStore.startPending();
      switch (role) {
        case UserRoleEnum.admin:
          await this.profileAdminService.updateProfile({
            ...profileInfo,
          } as AdminProfileInfo);
          break;
        case UserRoleEnum.parent:
          await this.profileParentService.updateProfile({
            ...profileInfo,
          } as ParentProfileInfo);
          break;
        case UserRoleEnum.student:
          await this.profileStudentService.updateProfile({
            ...profileInfo,
          } as StudentProfileInfo);
          break;
        case UserRoleEnum.supervisor:
          await this.profileSupervisorService.updateProfile({
            ...profileInfo,
          } as SupervisorProfileInfo);
          break;
        case UserRoleEnum.contenter:
          await this.profileContenterService.updateProfile({
            ...profileInfo,
          } as ContentMakerProfileInfo);
          break;
        case UserRoleEnum.assistant:
          await this.profileContenterService.updateProfile({
            ...profileInfo,
          } as AssistantProfileInfo);
          break;
        case UserRoleEnum.tutor:
          await this.profileTutorService.updateProfile({
            ...profileInfo,
          } as TutorProfileInfo);
          break;
        default:
          return assertUnreachable(role);
      }
      uiStore.notify({ message: "Готово!", options: { variant: "success" } });
    } catch (e) {
      if (e?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        ValidationErrorHandler.showErrorInfo("Произошла ошибка!", e.data.errors);
      } else {
        uiStore.notify({
          message: "Произошла ошибка! Попробуйте перезагрузить страницу.",
          options: { variant: "error" },
        });
      }
    } finally {
      uiStore.stopPending();
    }
  };

  setCreatedUser = (users: Record<string, User>) => {
    formatUsersPhone(users);
    this.users = { ...this.users, ...users };
  };

  setUsers = (users: Record<string, User>) => {
    formatUsersPhone(users);
    this.users = { ...users };
  };

  setResources = (normalizedResources: Exclude<NormalizedResources, "page">) => {
    const {
      users,
      users_profiles,
      users_profiles_parents,
      users_profiles_students,
      users_profiles_supervisors,
      users_profiles_tutors,
      users_profiles_admins,
      users_profiles_assistants,
      users_profiles_contenters,
    } = normalizedResources;
    this.setUsers(users);
    this.usersProfiles = { ...users_profiles };
    this.usersProfilesParents = { ...users_profiles_parents };
    this.usersProfilesStudents = { ...users_profiles_students };
    this.usersProfilesSupervisors = { ...users_profiles_supervisors };
    this.usersProfilesTutors = { ...users_profiles_tutors };
    this.usersProfilesAdmins = { ...users_profiles_admins };
    this.usersProfilesAssistants = { ...users_profiles_assistants };
    this.usersProfilesContenters = { ...users_profiles_contenters };
  };

  setUserProfiles = (userId: string, data: ProfilesIdentifier[]) => {
    const user = this.users[userId];
    const currentProfiles = Array.isArray(user.profiles) ? user.profiles : [];
    const ids = new Set(currentProfiles.map((profile) => profile.id));
    this.users[userId].profiles = [...currentProfiles, ...data.filter((profile) => !ids.has(profile.id))];
  };

  setAuthenticatedUserPermissions = (users: Record<string, User>, permissions: PermissionsUserInfo) => {
    const { authenticatedUser } = authStore;
    const authenticatedUserId = Object.keys(authenticatedUser)[0];
    if (Object.keys(users).some((id) => id === authenticatedUserId)) {
      authStore.setPermissions(permissions);
    }
  };
}
